/**
 * Release countdowns. Any element with [data-countdown] and a data-release time (Unix seconds, from Liquid) shows
 * how long until release, and optionally a pre-release first (data-prerelease):
 *   "Pre-release in 12d 4h" -> "Releases in 6d 3h" -> "Out now", then it hides itself after data-out-days days.
 * The time is split into parts (.cd-label, .cd-time, .cd-extra) so tight layouts can hide the label or the hours.
 * data-format="short" gives compact plain text for tight spots such as menu badges ("12d", "5h 20m", "Out now").
 * The element's own text (the date) stays as the fallback until this runs.
 */

const DAY = 86400;
const HOUR = 3600;

/** @param {number} seconds @returns {[string, string]} the main unit and the smaller one after it */
function units(seconds) {
  const days = Math.floor(seconds / DAY);
  const hours = Math.floor((seconds % DAY) / HOUR);
  const minutes = Math.floor((seconds % HOUR) / 60);
  if (days > 0) return [`${days}d`, `${hours}h`];
  if (hours > 0) return [`${hours}h`, `${minutes}m`];
  return [`${Math.max(minutes, 1)}m`, ''];
}

/** @param {number} seconds */
function shortText(seconds) {
  const [main, extra] = units(seconds);
  return seconds >= 7 * DAY || !extra ? main : `${main} ${extra}`;
}

/** @param {string} label @param {number} seconds */
function longHtml(label, seconds) {
  const [main, extra] = units(seconds);
  const extraHtml = extra ? `<span class="cd-extra"> ${extra}</span>` : '';
  return `<span class="cd-label">${label} </span><span class="cd-time">${main}${extraHtml}</span>`;
}

/** @param {HTMLElement} element @param {string} html */
function render(element, html) {
  if (element.dataset.cdShown === html) return;
  element.dataset.cdShown = html;
  element.innerHTML = html;
}

/** @param {HTMLElement} element @param {number} now */
function update(element, now) {
  const release = Number(element.dataset.release);
  if (!release) return;
  const prerelease = Number(element.dataset.prerelease) || 0;
  const short = element.dataset.format === 'short';
  const outDays = Number(element.dataset.outDays || 14);

  let html;
  if (prerelease && now < prerelease) {
    html = short ? shortText(prerelease - now) : longHtml('Pre-release in', prerelease - now);
  } else if (now < release) {
    html = short ? shortText(release - now) : longHtml('Releases in', release - now);
  } else if (now < release + outDays * DAY) {
    html = 'Out now';
    element.classList.add('is-out');
  } else {
    element.hidden = true;
    return;
  }

  element.hidden = false;
  render(element, html);
}

function tick() {
  const now = Date.now() / 1000;
  document.querySelectorAll('[data-countdown]').forEach((element) => update(/** @type {HTMLElement} */ (element), now));
}

tick();
setInterval(tick, 30000);
document.addEventListener('shopify:section:load', tick);
if (document.readyState !== 'complete') window.addEventListener('load', tick, { once: true });

/** @param {Node} node */
const touchesCountdown = (node) => {
  const element = node instanceof Element ? node : node.parentElement;
  if (!element) return false;
  return Boolean(element.closest('[data-countdown]') || element.querySelector('[data-countdown]'));
};

// The header re-renders parts of its menu after load (overflow list, mobile drawer, morphing section updates), which can
// add new badges or put the date text back, so re-run whenever a countdown is added or its text changes. A badge whose
// text was put back loses its cached markup, so it is drawn again.
let queued = false;
new MutationObserver((records) => {
  if (queued) return;
  const changed = records.some((record) =>
    record.type === 'characterData'
      ? touchesCountdown(record.target)
      : [...record.addedNodes].some(touchesCountdown)
  );
  if (!changed) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    document.querySelectorAll('[data-countdown]').forEach((element) => {
      const el = /** @type {HTMLElement} */ (element);
      if (el.dataset.cdShown !== undefined && el.innerHTML !== el.dataset.cdShown) delete el.dataset.cdShown;
    });
    tick();
  });
}).observe(document.body, { childList: true, subtree: true, characterData: true });